<template>
  <div class="full-screen-background">
    <div class="alquimia-container">
      <div class="alquimia-grid">
        <h2 class="titulo">
          {{ t('alchemy.title') }}
        </h2>

        <div class="main-area">
          <!-- Painel lateral de Batch -->
          <div class="batch-panel">
            <label>{{ t('alchemy.batchLabel', 'Inserir em lote') }}</label>
            <input
              v-model="batchID"
              @input="onBatchIDInput"
              placeholder="Digite o ID do item"
              class="batch-input"
            />
            <img
              v-if="batchPreviewTexture"
              :src="batchPreviewTexture"
              alt="Preview do item"
              class="batch-preview"
            />
            <div v-if="batchSelectedCells.length" class="batch-buttons">
              <v-btn small color="primary" @click="confirmBatchInsertion">
                {{ t('alchemy.confirmBatch', 'Confirmar inserção de lotes') }}
              </v-btn>
              <v-btn small color="error" @click="cancelBatchInsertion">
                {{ t('alchemy.cancelBatch', 'Cancelar inserção de lotes') }}
              </v-btn>
            </div>
          </div>

          <!-- Área de montagem da imagem -->
          <div class="template-wrapper" ref="wrapperRef">
            <img
              ref="imgRef"
              :src="templateImage"
              :alt="t('alchemy.alt')"
              class="template-img"
              @load="updateImageSize"
            />

            <!-- Sobreposição de cada célula -->
            <template v-for="(rodada, rowIndex) in rodadas" :key="'row' + rowIndex">
              <template v-for="(_, colIndex) in rodada" :key="'col' + colIndex">
                <img
                  v-if="itemTextures[rowIndex][colIndex]"
                  :src="itemTextures[rowIndex][colIndex]"
                  :style="getImageStyle(rowIndex, colIndex)"
                  class="cell-image"
                />
                <div
                  v-else
                  class="item-valor"
                  :style="getTextStyle(rowIndex, colIndex)"
                >
                  {{ rodadas[rowIndex][colIndex] }}
                </div>
                <div
                  class="cell-overlay"
                  :class="{
                    selected: isCellSelected(rowIndex, colIndex),
                    duplicate: isDuplicateCell(rowIndex, colIndex)
                  }"
                  :style="getOverlayStyle(rowIndex, colIndex)"
                  @click="selectCell(rowIndex, colIndex)"
                ></div>
              </template>
            </template>

            <!-- Botão Regras com tooltip -->
            <div class="rules-container">
              <h3>{{ t('alchemy.regras.title') }}</h3>
              <div class="rules-tooltip" style="width: 700px; background-color: black; font-size: 15px;">
                <!-- Intro (string simples) -->
                <p>{{ t('alchemy.regras.forbiddenIntro') }}</p>

                <!-- Lista de itens proibidos (array retornado com returnObjects) -->
                <ul>
                  <li>
                    {{t('alchemy.regras.forbiddenItems')}}
                  </li>
                </ul>

                <!-- Conclusão (string simples) -->
                <p>{{ t('alchemy.regras.conclusion') }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Campos manuais abaixo -->
        <div class="rodadas">
          <div
            class="rodada"
            v-for="(rodada, rowIndex) in rodadas"
            :key="'inp' + rowIndex"
          >
            <label>{{ t('alchemy.roundLabel', { n: rowIndex + 1 }) }}</label>
            <div class="input-wrapper">
              <input
                v-for="(_, colIndex) in rodada"
                :key="'inp' + colIndex"
                v-model="rodadas[rowIndex][colIndex]"
                type="text"
                :class="['campo-input', { 'highlight': isDuplicateCell(rowIndex, colIndex) }]"
                :placeholder="t('alchemy.itemPlaceholder', { n: colIndex + 1 })"
                @keydown.enter.prevent="onKeyFieldExit(rowIndex, colIndex)"
                @keydown.tab.prevent="onKeyFieldExit(rowIndex, colIndex)"
                @blur="onBlurFieldExit(rowIndex, colIndex)"
                @mousedown="onManualFieldMouseDown(rowIndex, colIndex)"
                @change="processField(rowIndex, colIndex)"
                :ref="el => inputsRefs[rowIndex][colIndex] = el"
              />
            </div>
          </div>
        </div>

        <!-- Botão de envio -->
        <v-btn
          color="green darken-2"
          dark
          class="mt-4"
          style="display: flex; margin: auto;"
          :loading="sending"
          @click="enviarSugestao"
        >
          {{ t('alchemy.sendButton') }}
        </v-btn>

        <!-- Modal de repetição -->
        <div v-if="showDuplicateModal" class="modal-overlay">
          <div class="modal-content">
            <p>Alquimias de jogador não devem ter itens repetidos na mesma rodada.</p>
            <v-btn color="primary" @click="closeDuplicateModal">
              OK
            </v-btn>
          </div>
        </div>

        <!-- Modal de confirmação para cancelar lote -->
        <div v-if="showModal" class="modal-overlay">
          <div class="modal-content">
            <p>{{ t('alchemy.cancelBatchConfirm', 'Deseja cancelar inserção em lote?') }}</p>
            <div class="modal-buttons">
              <v-btn color="primary" @click="confirmCancelBatch">
                {{ t('alchemy.yes', 'Sim, cancelar lote') }}
              </v-btn>
              <v-btn color="grey" @click="showModal = false">
                {{ t('alchemy.no', 'Não') }}
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Modal de slots vazios -->
        <div v-if="showEmptyModal" class="modal-overlay">
          <div class="modal-content">
            <p>{{ t(
              'alchemy.fillAllSlots',
              'Por favor, preencha todos os slots antes de enviar a alquimia.'
            ) }}</p>
            <v-btn color="primary" @click="closeEmptyModal">
              OK
            </v-btn>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import * as THREE from 'three'
import html2canvas from 'html2canvas'
import templateImage from '@/assets/alquimia/template.png'
import { sendAlquimiaSuggestion } from '@/services/api.js'

const { t } = useI18n()

const RODADAS = 5
const COLUNAS = 8

// estado das rodadas e texturas
const rodadas       = reactive(Array.from({ length: RODADAS }, () => Array(COLUNAS).fill('')))
const itemTextures  = reactive(Array.from({ length: RODADAS }, () => Array(COLUNAS).fill(null)))
const inputsRefs    = reactive(Array.from({ length: RODADAS }, () => Array(COLUNAS).fill(null)))

// refs para imagem e wrapper
const imgRef     = ref(null)
const wrapperRef = ref(null)
let escala = 1

// Batch mode
const batchID             = ref('')
const batchPreviewTexture = ref(null)
const batchSelectedCells  = reactive([])
const showModal           = ref(false)
const modalRow            = ref(null)
const modalCol            = ref(null)

// Duplicate check
const showDuplicateModal  = ref(false)
const duplicateCells      = reactive([])

// Empty slots modal
const showEmptyModal      = ref(false)

// Carregamento de INIs
async function loadIni(path) {
  const res = await fetch(path)
  const txt = await res.text()
  return txt
    .split('\n').map(l => l.trim()).filter(Boolean)
    .map(l => l.split('|')).filter(p => p.length > 1)
    .map(([id, texture]) => ({ id, texture }))
}
const promiseCItems    = loadIni('/auxiliadores/C_Item.ini')
const promiseCItemMall = loadIni('/auxiliadores/C_ItemMall.ini')

// Batch functions
async function onBatchIDInput() {
  const id = batchID.value.trim()
  if (!id) {
    batchPreviewTexture.value = null
    return
  }
  const [cItems, cItemMall] = await Promise.all([promiseCItems, promiseCItemMall])
  const banco = [...cItems, ...cItemMall]
  const achado = banco.find(el => el.id === id)
  if (achado) {
    const texturePath = `/auxiliadores/itemicon/${achado.texture}.png`
    try {
      const url = await carregarMiniatura(texturePath)
      batchPreviewTexture.value = url
    } catch {
      batchPreviewTexture.value = null
    }
  } else {
    batchPreviewTexture.value = null
  }
}

function isCellSelected(row, col) {
  return batchSelectedCells.some(c => c.row === row && c.col === col)
}

function isDuplicateCell(row, col) {
  return duplicateCells.some(c => c.row === row && c.col === col)
}

function selectCell(row, col) {
  if (!batchPreviewTexture.value) return
  const idx = batchSelectedCells.findIndex(c => c.row === row && c.col === col)
  if (idx >= 0) batchSelectedCells.splice(idx, 1)
  else batchSelectedCells.push({ row, col })
}

function cancelBatchInsertion() {
  batchID.value = ''
  batchPreviewTexture.value = null
  batchSelectedCells.splice(0)
}

function confirmBatchInsertion() {
  for (const { row, col } of batchSelectedCells) {
    rodadas[row][col] = batchID.value
    itemTextures[row][col] = batchPreviewTexture.value
  }
  cancelBatchInsertion()
}

function onManualFieldMouseDown(row, col) {
  if (batchSelectedCells.length) {
    modalRow.value = row
    modalCol.value = col
    showModal.value = true
  }
}

async function confirmCancelBatch() {
  cancelBatchInsertion()
  showModal.value = false
  await nextTick()
  inputsRefs[modalRow.value][modalCol.value]?.focus()
}

// Single‐field processing
async function processField(row, col) {
  const id = rodadas[row][col].trim()
  if (!id) return
  const [cItems, cItemMall] = await Promise.all([promiseCItems, promiseCItemMall])
  const banco = [...cItems, ...cItemMall]
  const achado = banco.find(el => el.id === id)
  if (achado) {
    const texturePath = `/auxiliadores/itemicon/${achado.texture}.png`
    try {
      const url = await carregarMiniatura(texturePath)
      itemTextures[row][col] = url
    } catch (err) {
      console.error(err)
    }
  }
}

async function onKeyFieldExit(row, col) {
  if (!batchSelectedCells.length) await processField(row, col)
  focusNext(row, col)
}

function onBlurFieldExit(row, col) {
  if (!batchSelectedCells.length) processField(row, col)
}

function focusNext(row, col) {
  let nextRow = row, nextCol = col + 1
  if (nextCol >= COLUNAS) {
    nextCol = 0
    nextRow = row + 1
  }
  if (nextRow < RODADAS) {
    inputsRefs[nextRow][nextCol]?.focus()
  }
}

function carregarMiniatura(path) {
  return new Promise((resolve, reject) => {
    const loader = new THREE.TextureLoader()
    loader.load(
      path,
      tex => resolve(tex.image.currentSrc),
      undefined,
      err => reject(err)
    )
  })
}

function updateImageSize() {
  const natW  = imgRef.value.naturalWidth
  const dispW = imgRef.value.clientWidth
  escala = dispW / natW
}

function getBasePosition(row, col) {
  const startX = 215, startY = 150
  const cellW  = 50,  cellH  = 50
  const paddingHorizontal = 5
  return {
    x: (startX + col * (cellW + paddingHorizontal)) * escala,
    y: (startY + row * cellH) * escala
  }
}

function getTextStyle(row, col) {
  const { x, y } = getBasePosition(row, col)
  return {
    position:    'absolute',
    left:        `${x}px`,
    top:         `${y}px`,
    width:       `${100 * escala}px`,
    height:      `${100 * escala}px`,
    textAlign:   'center',
    fontWeight:  'bold',
    fontSize:    `${14 * escala}px`,
    color:       'blue',
    lineHeight:  `${30 * escala}px`,
    transform:   'translate(-50%, -50%)',
  }
}

function getImageStyle(row, col) {
  const { x, y } = getBasePosition(row, col)
  const lado = 38 * escala
  return {
    position:     'absolute',
    left:         `${x}px`,
    top:          `${y}px`,
    width:        `${lado}px`,
    height:       `${lado}px`,
    objectFit:    'contain',
    pointerEvents:'none',
    transform:    'translate(-50%, -50%)'
  }
}

function getOverlayStyle(row, col) {
  const cellW  = 50, cellH = 50
  const paddingHorizontal = 5
  const { x, y } = getBasePosition(row, col)
  const fullW = (cellW + paddingHorizontal) * escala
  const fullH = cellH * escala

  return {
    position:  'absolute',
    left:      `${x}px`,
    top:       `${y}px`,
    width:     `${fullW}px`,
    height:    `${fullH}px`,
    transform: 'translate(-50%, -50%)',
    cursor:    batchPreviewTexture.value ? 'pointer' : 'default',
    zIndex:    2
  }
}

// Envio
const sending = ref(false)

function checkDuplicates() {
  duplicateCells.splice(0)
  let found = false
  rodadas.forEach((row, r) => {
    const counts = {}
    row.forEach((id, c) => {
      const val = id.trim()
      if (!val) return
      counts[val] = counts[val] ? [...counts[val], c] : [c]
    })
    Object.values(counts).forEach(cols => {
      if (cols.length > 1) {
        found = true
        cols.forEach(c => duplicateCells.push({ row: r, col: c }))
      }
    })
  })
  if (found) {
    showDuplicateModal.value = true
  }
  return !found
}

async function enviarSugestao() {
  // 1) Verifica duplicatas
  if (!checkDuplicates()) return

  // 2) Verifica slots vazios
  const hasEmpty = rodadas.some(row => row.some(cell => !cell.trim()))
  if (hasEmpty) {
    // alerta original
    console.log('')
    // exibe modal
    showEmptyModal.value = true
    return
  }

  // 3) Se tudo ok, envia
  try {
    sending.value = true
    const token = localStorage.getItem('jwt_token')
    if (!token) throw new Error(t('alchemy.loginError'))

    const canvas      = await html2canvas(wrapperRef.value)
    const imageBase64 = canvas.toDataURL('image/png')
    const payload     = JSON.parse(atob(token.split('.')[1]))
    const mid         = payload.mid || 'desconhecido'

    await sendAlquimiaSuggestion(token, mid, rodadas, imageBase64)
    alert(t('alchemy.successMessage'))
    rodadas.forEach(r => r.fill(''))
    itemTextures.forEach(r => r.fill(null))
  } catch (err) {
    console.error(err)
    alert(err.message || t('alchemy.sendError'))
  } finally {
    sending.value = false
  }
}

function closeDuplicateModal() {
  showDuplicateModal.value = false
}

function closeEmptyModal() {
  showEmptyModal.value = false
}
</script>

<style scoped>
.full-screen-background {
  background-image: url('@/assets/background/fundotrocasenha.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  min-height: 100vh;
  width: 100%;
  position: relative;
}
.alquimia-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 40px;
  background-color: var(--bg-color);
}
.alquimia-grid {
  font-family: Arial, sans-serif;
  background-color: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 6px 24px rgba(0,0,0,0.1);
  padding: 10px;
  max-width: 1200px;
  width: 100%;
}
.titulo {
  text-align: center;
  font-size: 24px;
  margin-bottom: 30px;
  font-weight: bold;
  color: var(--text-color);
}
.main-area {
  display: flex;
  gap: 20px;
  justify-content: center;
  align-items: center;
}
.batch-panel {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.batch-input {
  padding: 6px;
  font-size: 14px;
}
.batch-preview {
  max-width: 100%;
  border: 1px solid #ccc;
  border-radius: 4px;
}
.batch-buttons {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
}
.template-wrapper {
  position: relative;
  width: 100%;
  max-width: 600px;
}
.template-img {
  width: 100%;
  display: block;
  opacity: 0.9;
}
.rules-container {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 5;
}
.rules-label {
  font-weight: bold;
  cursor: pointer;
}
.rules-tooltip {
  display: none;
  position: absolute;
  top: 20px;
  right: 0;
  background: var(--card-bg);
  color: var(--text-color);
  padding: 10px;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  max-width: 240px;
  font-size: 12px;
  line-height: 1.4;
}
.rules-container:hover .rules-tooltip {
  display: block;
}
.cell-overlay {
  pointer-events: all;
}
.cell-overlay.selected {
  background-color: rgba(0,128,0,0.3);
}
.cell-overlay.duplicate {
  background-color: rgba(255,0,0,0.3);
}
.rodadas {
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.rodada {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.rodada label {
  width: 90px;
  font-weight: bold;
}
.input-wrapper {
  position: relative;
}
.campo-input {
  width: 115px;
  height: 32px;
  text-align: center;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 4px;
}
.campo-input.highlight {
  border-color: red;
  box-shadow: 0 0 4px rgba(255,0,0,0.7);
}
.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}
.modal-content {
  background-color: rgba(0, 0, 0, 0.664);
  color: var(--text-color);
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  min-width: 280px;
}
.modal-buttons {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 16px;
}
</style>
